// WikiDoc.jsx — document reader/editor surface. Orchestrates block render +
// edit mode, version history/diff, read tracker, permissions panel, read-confirm bar.
import React, { useEffect, useState, useCallback } from 'react';
import {
    Pencil, Check, History, UserCheck, Shield, UserPlus, Lock, ArrowRight, Send, FileClock,
    Archive, Trash2, MoreHorizontal, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useWikiApi } from '../../hooks/useWiki';
import { DEPT_BY_ID, relativeTime } from '../../lib/wikiConstants';
import WikiBlock from './blocks/WikiBlock';
import WikiBlockEditor from './blocks/WikiBlockEditor';
import WikiVersionPanel, { WikiDiffView } from './WikiVersionPanel';
import WikiReadTracker, { WikiReadConfirmBar } from './WikiReadTracker';
import WikiPermsPanel from './WikiPermsPanel';

export default function WikiDoc({ pageId, onBack, onOpenAgent, onChanged, onLoaded, onArchive, onDelete, onRestore }) {
    const api = useWikiApi();
    const [page, setPage] = useState(null);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [working, setWorking] = useState([]);
    const [saving, setSaving] = useState(false);
    const [panel, setPanel] = useState(null);     // 'history' | 'readers' | 'perms'
    const [diff, setDiff] = useState(null);        // { a, b }
    const [confirmed, setConfirmed] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);

    const load = useCallback(() => {
        setError(null);
        api.page(pageId).then(p => { setPage(p); setConfirmed(!!p.read); onLoaded && onLoaded(p.dept); }).catch(err => {
            setError(err?.response?.data?.detail || { reason: 'no_access' });
            setPage(null);
        });
    }, [api, pageId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setEditing(false); setPanel(null); setDiff(null); load(); }, [pageId]); // eslint-disable-line

    const startEdit = () => { setWorking(JSON.parse(JSON.stringify(page.blocks || []))); setEditing(true); setPanel(null); };
    const save = async () => {
        setSaving(true);
        try {
            const r = await api.saveBlocks(pageId, { blocks: working });
            setEditing(false);
            if (!r.noop) toast.success(`Saved · v${r.version}`);
            load(); onChanged && onChanged();
        } catch { toast.error('Could not save changes'); }
        finally { setSaving(false); }
    };
    const setStatus = async (status) => {
        try {
            await api.updatePage(pageId, { status });
            toast.success(status === 'published' ? 'Published' : 'Moved to draft');
            load(); onChanged && onChanged();
        } catch { toast.error('Could not update status'); }
    };

    if (error) {
        const reason = typeof error === 'object' ? error.reason : 'no_access';
        return (
            <div className="h-full flex items-center justify-center p-10">
                <div className="text-center max-w-sm">
                    <div className="w-12 h-12 rounded-full bg-stone-100 mx-auto flex items-center justify-center text-stone-500"><Lock className="w-5 h-5" /></div>
                    <div className="wiki-display font-bold text-stone-900 mt-3">You can’t open this page</div>
                    <div className="text-[13px] text-stone-500 mt-1">{reason === 'restricted' ? 'This page is restricted at the page level.' : 'This page isn’t shared with your role.'}</div>
                    <button onClick={onBack} className="mt-4 h-9 px-4 rounded-lg border border-stone-200 text-[12px] font-semibold">Back</button>
                </div>
            </div>
        );
    }
    if (!page) return <div className="h-full flex items-center justify-center text-stone-400 text-[13px]">Loading…</div>;
    if (diff) return <WikiDiffView pageId={pageId} a={diff.a} b={diff.b} onBack={() => { setDiff(null); setPanel('history'); }} />;

    const dept = DEPT_BY_ID[page.dept];

    return (
        <div className="h-full flex">
            <div className="flex-1 flex flex-col min-w-0">
                {page.archived && (
                    <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-[12px] text-amber-800">
                        <Archive className="w-3.5 h-3.5 shrink-0" />
                        <span className="flex-1">This page is archived — it’s hidden from the tree.</span>
                        {page.can_edit && (
                            <button onClick={async () => { await onRestore?.('page', pageId); load(); }}
                                className="h-7 px-2.5 rounded-md bg-white border border-amber-300 text-amber-800 text-[11px] font-semibold inline-flex items-center gap-1 hover:bg-amber-100"><RotateCcw className="w-3 h-3" /> Restore</button>
                        )}
                    </div>
                )}
                <div className="px-6 pt-4 pb-3 border-b border-stone-200">
                    <div className="flex items-center gap-1.5 text-[11px] text-stone-500 mb-2">
                        <button onClick={onBack} className="hover:text-stone-900 inline-flex items-center gap-1">{dept?.icon && <dept.icon className="w-3 h-3" />}{dept?.name}</button>
                        {(page.path || []).map((p, i) => <React.Fragment key={i}><span className="text-stone-400">/</span><span>{p}</span></React.Fragment>)}
                        <span className="text-stone-400">/</span><span className="text-stone-600 font-medium">{page.name}</span>
                    </div>
                    <div className="flex items-start gap-3">
                        <h1 className="wiki-display font-extrabold text-stone-900 text-[24px] tracking-tight flex-1">{page.name}</h1>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {page.can_edit && page.status === 'draft' && !editing && (
                                <button onClick={() => setStatus('published')}
                                    className="h-8 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                                    <Send className="w-3.5 h-3.5" /> Publish
                                </button>
                            )}
                            {page.can_edit && page.status !== 'draft' && !editing && (
                                <button onClick={() => setStatus('draft')} title="Move back to draft (hidden from others until re-published)"
                                    className="h-8 px-2.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 border border-stone-200 text-stone-600 hover:bg-stone-50">
                                    <FileClock className="w-3.5 h-3.5" /> Unpublish
                                </button>
                            )}
                            {page.can_edit && (
                                <button onClick={() => editing ? save() : startEdit()} disabled={saving}
                                    className={`h-8 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 ${editing ? 'bg-stone-900 text-white' : 'border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                                    {editing ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}{editing ? (saving ? 'Saving…' : 'Done') : 'Edit'}
                                </button>
                            )}
                            <button onClick={() => setPanel(p => p === 'history' ? null : 'history')} title="Version history"
                                className={`h-8 w-8 rounded-lg border border-stone-200 flex items-center justify-center ${panel === 'history' ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-50'}`}><History className="w-4 h-4" /></button>
                            {page.read_required && (
                                <button onClick={() => setPanel(p => p === 'readers' ? null : 'readers')} title="Read tracker"
                                    className={`h-8 w-8 rounded-lg border border-stone-200 flex items-center justify-center ${panel === 'readers' ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-50'}`}><UserCheck className="w-4 h-4" /></button>
                            )}
                            {page.can_admin && (
                                <button onClick={() => setPanel(p => p === 'perms' ? null : 'perms')} title="Permissions"
                                    className={`h-8 w-8 rounded-lg border border-stone-200 flex items-center justify-center ${panel === 'perms' ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-50'}`}><Shield className="w-4 h-4" /></button>
                            )}
                            <button onClick={() => toast.info('Sharing is managed via Permissions for now')} title="Share"
                                className="h-8 w-8 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 flex items-center justify-center"><UserPlus className="w-4 h-4" /></button>
                            {page.can_edit && !editing && (
                                <div className="relative">
                                    <button onClick={() => setActionsOpen(o => !o)} title="More actions"
                                        className={`h-8 w-8 rounded-lg border border-stone-200 flex items-center justify-center ${actionsOpen ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-50'}`}><MoreHorizontal className="w-4 h-4" /></button>
                                    {actionsOpen && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setActionsOpen(false)} />
                                            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-stone-200 wiki-shadow-float p-1 z-50">
                                                {!page.archived && (
                                                    <button onClick={() => { setActionsOpen(false); onArchive?.('page', pageId, page.name); }}
                                                        className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left text-[13px] text-stone-700 hover:bg-stone-50"><Archive className="w-4 h-4 text-stone-500" /> Archive</button>
                                                )}
                                                {page.archived && (
                                                    <button onClick={async () => { setActionsOpen(false); await onRestore?.('page', pageId); load(); }}
                                                        className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left text-[13px] text-stone-700 hover:bg-stone-50"><RotateCcw className="w-4 h-4 text-stone-500" /> Restore</button>
                                                )}
                                                <button onClick={() => { setActionsOpen(false); onDelete?.('page', pageId, page.name); }}
                                                    className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left text-[13px] text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-500" /> Delete</button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-stone-500">
                        <span>{page.owner_name || '—'}</span><span>·</span>
                        <span>edited {relativeTime(page.updated_at)}</span><span>·</span>
                        <span className="font-mono">v{page.version}</span>
                        {page.status === 'draft' && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><FileClock className="w-2.5 h-2.5" /> Draft — only you &amp; admins can see this</span>}
                        {page.restricted && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><Lock className="w-2.5 h-2.5" /> Restricted</span>}
                        {page.read_required && <span className="px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-800 border border-stone-300">Required reading</span>}
                    </div>
                </div>

                <div className="flex-1 overflow-auto relative">
                    <div className="px-6 py-5 max-w-[720px] mx-auto pb-10">
                        {editing
                            ? <WikiBlockEditor blocks={working} onChange={setWorking} />
                            : (page.blocks || []).map((b, i) => <WikiBlock key={b.block_id || i} b={b} />)}
                        {!editing && (
                            <button onClick={() => onOpenAgent(page.dept)} className="mt-8 w-full wiki-ai-border rounded-xl bg-white p-3 flex items-center gap-3 text-left hover:bg-stone-100/30 transition">
                                <div className="w-8 h-8 rounded-lg wiki-grad flex items-center justify-center wiki-shadow-ai shrink-0">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" /></svg>
                                </div>
                                <div className="flex-1"><div className="text-[13px] font-semibold wiki-grad-text">Ask the {dept?.name} Agent about this page</div><div className="text-[11px] text-stone-500">Get a cited answer without reading the whole document.</div></div>
                                <ArrowRight className="w-4 h-4 text-stone-600" />
                            </button>
                        )}
                    </div>
                </div>

                {page.read_required && !editing && (
                    <WikiReadConfirmBar pageId={pageId} confirmed={confirmed}
                        onConfirmed={() => { setConfirmed(true); onChanged && onChanged(); }}
                        onShowReaders={() => setPanel('readers')} />
                )}
            </div>

            {panel === 'history' && <WikiVersionPanel pageId={pageId} onClose={() => setPanel(null)} onCompare={(a, b) => setDiff({ a, b })} />}
            {panel === 'readers' && <WikiReadTracker pageId={pageId} isAdmin={page.can_admin} onClose={() => setPanel(null)} />}
            {panel === 'perms' && <WikiPermsPanel deptId={page.dept} pageRestricted={page.restricted} onClose={() => setPanel(null)} />}
        </div>
    );
}
